import { RichTextInput } from '@/components/RichTextInput';
import { alert } from '@/services/alert';
import {
  ActionIcon,
  Box,
  Button,
  Center,
  Divider,
  FileButton,
  Flex,
  Group,
  Image,
  Radio,
  Select,
  LoadingOverlay,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { Organization, Patient, Practitioner } from '@medplum/fhirtypes';
import { ChangeEvent, ReactElement, useMemo, useRef, useState, Fragment, useEffect } from 'react';
import { IconSend, IconTrash, IconFile, IconCloudUpload } from '@tabler/icons-react';
import { sendFax } from '@/services/faxing';
import { formatFileSize } from '@/services/attachments';
import PatientCardSelector from '@/components/PatientCardSelector';
import { formatPhoneNumber, normalizePhoneNumber } from '@bariendo/lib/phone-validations';
import { createOrganizationOrGetExisting } from '@/services/organization';
import { checkFaxValidation } from '@/components/EditableField/services';

interface CreateFaxFormProps {
  onClose: () => void;
  initialPatient?: Patient;
  initialOrganization?: Organization;
}

const SelectOrganizationWithFaxNumbers = ({
  allOrganizations,
  selectedOrganization,
  isParentLoading,
  setSelectedOrganization,
}: {
  allOrganizations: Organization[];
  selectedOrganization: Organization | null;
  isParentLoading: boolean;
  setSelectedOrganization: (organization: Organization | null) => void;
}) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(isParentLoading);

  useEffect(() => {
    if (isParentLoading) {
      setOrganizations([]);
      setLoading(true);
      return;
    }

    const organizationsWithFaxNumbers = allOrganizations.filter((organization) =>
      organization.contact?.some((contact) => contact.telecom?.some((telecom) => telecom.system === 'fax'))
    );
    setOrganizations(organizationsWithFaxNumbers);
    setLoading(false);
  }, [allOrganizations, isParentLoading]);

  const handleOrganizationSelectChange = (value: string | null) => {
    const organization = organizations.find((organization) => organization.id === value);
    setSelectedOrganization(organization || null);
  };

  return (
    <Select
      data={organizations.map((organization) => ({ value: organization.id || '', label: organization.name || '' }))}
      placeholder="Select partner"
      value={selectedOrganization?.id ?? null}
      onChange={handleOrganizationSelectChange}
      disabled={loading}
      searchable
    />
  );
};

export function CreateFaxForm({ onClose, initialPatient, initialOrganization }: CreateFaxFormProps): ReactElement {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const fileInputRef = useRef<HTMLButtonElement>(null);
  const textareaInputRef = useRef<HTMLDivElement>(null);

  const [allOrganizations, setAllOrganizations] = useState<Organization[]>([]);
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(true);
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(initialOrganization ?? null);
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string | null>(null);
  const [manualFaxNumber, setManualFaxNumber] = useState<string>('');
  const [manualFaxError, setManualFaxError] = useState<string | undefined>(undefined);
  const [organizationName, setOrganizationName] = useState<string>('');
  const [useManualPhoneEntry, setUseManualPhoneEntry] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | undefined>(initialPatient);
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [richTextContent, setRichTextContent] = useState<any>('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);

  // Get phone numbers from selected organization
  const organizationFaxNumbers = useMemo(() => {
    if (!selectedOrganization?.contact) return [];
    const faxNumbers =
      selectedOrganization.contact
        .filter((contact) => contact.telecom?.some((telecom) => telecom.system === 'fax'))
        .flatMap((contact) => {
          return (
            contact.telecom
              ?.filter((telecom) => telecom.system === 'fax' && telecom.value)
              .map((telecom) => ({
                value: telecom.value!,
                label: telecom.value!,
              })) || []
          );
        }) || [];
    return faxNumbers.map((fax) => formatPhoneNumber(fax.value));
  }, [selectedOrganization]);

  useEffect(() => {
    if (!selectedOrganization) {
      return;
    }

    setSelectedPhoneNumber((currentNumber) => {
      if (currentNumber && organizationFaxNumbers.includes(currentNumber)) {
        return currentNumber;
      }
      return organizationFaxNumbers[0] ?? null;
    });
  }, [organizationFaxNumbers, selectedOrganization]);

  useEffect(() => {
    if (initialPatient) {
      setSelectedPatient(initialPatient);
    }
  }, [initialPatient]);

  useEffect(() => {
    if (initialOrganization) {
      setSelectedOrganization(initialOrganization);
    }
  }, [initialOrganization]);

  const handleFileSelect = (selectedFiles: File[] | null) => {
    if (selectedFiles && selectedFiles.length > 0) {
      setFiles((prev) => [...prev, ...selectedFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if (isSending) return;

    let organization = useManualPhoneEntry ? undefined : selectedOrganization;

    const normalizedManualFaxNumber = normalizePhoneNumber(manualFaxNumber);
    if (useManualPhoneEntry) {
      organization = await createOrganizationOrGetExisting(medplum, organizationName, normalizedManualFaxNumber);
    }

    setIsSending(true);
    try {
      await sendFax(
        medplum,
        organization,
        useManualPhoneEntry ? normalizedManualFaxNumber : selectedPhoneNumber,
        files,
        content,
        subject,
        profile as Practitioner,
        selectedPatient
      );

      alert.success('Fax sent successfully');
      onClose();
    } catch (error) {
      alert.error(`Failed to send fax: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleManualPhoneEntryChange = (value: string) => {
    const isPhoneMode = value === 'phone';
    setUseManualPhoneEntry(isPhoneMode);
    if (isPhoneMode) {
      setSelectedOrganization(null);
      setSelectedPhoneNumber(null);
    } else {
      setManualFaxNumber('');
      setOrganizationName('');
    }
  };

  const handleManualFaxNumberChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setManualFaxNumber(value);
    setManualFaxError(checkFaxValidation(value));
    // Reset selected organization when manually typing
    if (value.trim()) {
      setSelectedOrganization(null);
      setSelectedPhoneNumber(null);
    }
  };

  const handleOrganizationSelectChange = (resource: Organization | null) => {
    setSelectedOrganization(resource);
    setSelectedPhoneNumber(null);
    // Reset manual input when organization is selected
    setManualFaxNumber('');
    setOrganizationName('');
  };

  useEffect(() => {
    const getAllOrganizations = async () => {
      setIsLoadingOrganizations(true);
      try {
        const byId = new Map<string, Organization>();
        for await (const organizationPage of medplum.searchResourcePages('Organization', {
          _count: 1000,
          _sort: 'name',
        })) {
          for (const organization of organizationPage) {
            if (organization.id) {
              byId.set(organization.id, organization);
            }
          }
        }
        setAllOrganizations(Array.from(byId.values()));
      } finally {
        setIsLoadingOrganizations(false);
      }
    };
    getAllOrganizations();
  }, [medplum]);

  const isManualPhoneEntryValid =
    manualFaxNumber.trim().length > 0 && !manualFaxError && organizationName.trim().length > 0;
  const isOrganizationSelectValid = selectedOrganization && selectedPhoneNumber && organizationFaxNumbers.length > 0;
  const isFormValid = useManualPhoneEntry ? isManualPhoneEntryValid : isOrganizationSelectValid;

  const showLoadingOverlay = !useManualPhoneEntry && isLoadingOrganizations;

  return (
    <Box pos="relative">
      <LoadingOverlay visible={showLoadingOverlay} overlayProps={{ backgroundOpacity: 0.55, blur: 2 }} zIndex={10} />
      <Stack gap="md">
        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Send to<span style={{ color: 'red' }}>*</span>
          </Text>
          <Box style={{ width: '386px' }}>
            <Radio.Group value={useManualPhoneEntry ? 'phone' : 'organization'} onChange={handleManualPhoneEntryChange}>
              <Group mt="xs" mb="xs">
                <Radio value="organization" label="Select partner" />
                <Radio value="phone" label="Enter fax number manually" />
              </Group>
            </Radio.Group>

            {useManualPhoneEntry ? (
              <Fragment>
                <TextInput
                  placeholder="(415) 555-0132"
                  value={manualFaxNumber}
                  mb="xs"
                  onChange={handleManualFaxNumberChange}
                  error={manualFaxError}
                  description="Include a country code (e.g. +44) for international fax numbers."
                  inputWrapperOrder={['label', 'input', 'error', 'description']}
                />

                <TextInput
                  placeholder="Partner name"
                  value={organizationName}
                  onChange={(e) => setOrganizationName(e.target.value)}
                  required
                />
              </Fragment>
            ) : (
              <Fragment>
                <SelectOrganizationWithFaxNumbers
                  allOrganizations={allOrganizations}
                  selectedOrganization={selectedOrganization}
                  isParentLoading={isLoadingOrganizations}
                  setSelectedOrganization={handleOrganizationSelectChange}
                />

                <Select
                  placeholder="Select fax number"
                  data={organizationFaxNumbers}
                  value={selectedPhoneNumber}
                  onChange={setSelectedPhoneNumber}
                  disabled={!selectedOrganization || organizationFaxNumbers.length === 0}
                  searchable
                  mt="xs"
                />
              </Fragment>
            )}
          </Box>
        </Stack>

        <Stack gap="xs" style={{ width: '386px' }}>
          <Text size="sm" fw={600}>
            Relate to patient
          </Text>
          <PatientCardSelector patient={selectedPatient} setSelectedPatient={setSelectedPatient} />
        </Stack>

        <Divider />
        <Text size="sm" fw={600}>
          Cover sheet
        </Text>

        <Stack gap="xs">
          <Text size="sm" fw={600}>
            Subject
          </Text>
          <TextInput placeholder="Subject line" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </Stack>

        <Stack gap="xs">
          <Text size="sm" fw={500}>
            Remarks / Message
          </Text>
          <Box style={{ minHeight: '200px' }}>
            <RichTextInput
              value={richTextContent}
              onChange={(text, json) => {
                setContent(text);
                setRichTextContent(json);
              }}
              textareaInputRef={textareaInputRef as React.RefObject<HTMLDivElement>}
              disabled={isSending}
              placeholder="Write a message"
              editorKey={`fax-form-${selectedPatient?.id || 'new'}`}
              contentMinHeight="160px"
              // 225px makes the modal avoid scrolling on 1080p
              contentMaxHeight="225px"
            />
          </Box>
        </Stack>

        {files.length > 0 && (
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              Attached files
            </Text>
            <Flex gap="xs" wrap="wrap">
              {files.map((file, index) => (
                <Box
                  key={index}
                  p="xs"
                  style={{
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    width: 'fit-content',
                    maxWidth: '300px',
                    position: 'relative',
                  }}
                >
                  <Group gap="sm" align="center" style={{ width: '100%' }}>
                    {file.type.startsWith('image/') ? (
                      <Image
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        style={{
                          width: '40px',
                          height: '40px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          flexShrink: 0,
                        }}
                      />
                    ) : (
                      <Center w={40} h={40}>
                        <IconFile size={20} color="#6c757d" />
                      </Center>
                    )}
                    <Box pr="xl" style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" truncate fw={500}>
                        {file.name}
                      </Text>
                      <Text size="xxs" c="dimmed">
                        {formatFileSize(file.size)}
                      </Text>
                    </Box>
                    <Tooltip label="Remove file" withArrow>
                      <ActionIcon
                        variant="subtle"
                        color="black"
                        onClick={() => handleRemoveFile(index)}
                        size="sm"
                        style={{ flexShrink: 0 }}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Box>
              ))}
            </Flex>
          </Stack>
        )}

        <Group justify="space-between" mt="md">
          <FileButton onChange={handleFileSelect} accept="application/pdf,image/jpg,image/jpeg,image/png" multiple>
            {(props) => (
              <Button {...props} variant="subtle" rightSection={<IconCloudUpload size={16} />} ref={fileInputRef}>
                Add file
              </Button>
            )}
          </FileButton>
          <Button
            rightSection={<IconSend size={16} />}
            onClick={handleSend}
            disabled={!isFormValid || isSending}
            loading={isSending}
          >
            Send
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
